"use client";

import React, { useState, useEffect } from 'react';
// Removed Lucide icon import as we use a custom SVG
// import { ArrowUp } from 'lucide-react';
// Removed Button component import
// import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils"; // Assuming cn utility exists

interface ScrollToTopButtonProps {
  isAtBottom?: boolean;
}

export const ScrollToTopButton = ({ isAtBottom = false }: ScrollToTopButtonProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [localIsAtBottom, setLocalIsAtBottom] = useState(isAtBottom);

  // Combined function to detect both visibility and bottom position.
  // Skip reading documentElement.scrollHeight when near the top of the page
  // (scrollTop <= 300) to avoid unnecessary forced layout reflows on load.
  const handleScroll = () => {
    const scrollTop = window.scrollY;

    // Visibility logic - show button after scrolling 300px
    if (scrollTop <= 300) {
      setIsVisible(false);
      setLocalIsAtBottom(false);
      return;
    }

    setIsVisible(true);

    // Bottom detection logic (only queried once the button is actually visible)
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    // Check if we're near the bottom (within 50px)
    const isBottom = scrollTop + windowHeight >= documentHeight - 50;
    setLocalIsAtBottom(isBottom);
  };

  // Function to scroll to top smoothly
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth', // Smooth scrolling animation
    });
  };

  // Use effect to initialize and update localIsAtBottom when the prop changes
  useEffect(() => {
    setLocalIsAtBottom(isAtBottom);
  }, [isAtBottom]);

  // Add scroll event listener (desktop-only), passive + rAF-throttled for better INP
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');

    let rafId = 0;
    let pending = false;

    const onScroll = () => {
      if (pending) return;
      pending = true;
      rafId = window.requestAnimationFrame(() => {
        pending = false;
        handleScroll();
      });
    };

    const attach = () => {
      if (mediaQuery.matches) {
        window.addEventListener('scroll', onScroll, { passive: true });
        // Initialize on attach
        handleScroll();
      }
    };

    const detach = () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
      pending = false;
    };

    attach();
    const onChange = () => {
      detach();
      attach();
    };
    mediaQuery.addEventListener('change', onChange);

    // Cleanup listener on component unmount
    return () => {
      mediaQuery.removeEventListener('change', onChange);
      detach();
    };
  }, []);

  // Dynamic position classes based on bottom position
  const bottomPositionClass = localIsAtBottom 
    ? "bottom-[10.5rem] md:bottom-8" 
    : "bottom-[4.5rem] md:bottom-8";

  return (
    <button
      onClick={scrollToTop}
      // Cursor pointer shows this arrow is clickable.
      className={cn(
        // Base styles: fixed position, z-index, flex centering, focus outline removed
        "fixed z-50 flex items-center justify-center focus:outline-none right-8 cursor-pointer",
        // Dynamic bottom positioning based on scroll position
        bottomPositionClass,
        // Transition for smoother position changes
        "transition-all duration-300 ease-out",
        // Visibility control
        isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      )}
      aria-label="Scroll to top"
      tabIndex={isVisible ? 0 : -1} // Make button focusable only when visible
    >
      {/* Relative container for positioning inner elements */}
      <div className="relative">
        {/* Absolute div for gradient background, blur, and pulse animation */}
        <div className="absolute inset-0 rounded-full bg-[#8AD4E1] blur-md opacity-90" />

        {/* Relative div for the main icon container */}
        <div className="relative w-12 h-12 rounded-full bg-black border border-white/20 flex items-center justify-center shadow-lg backdrop-blur-sm">
          {/* Custom SVG Icon */}
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="text-[#8AD4E1]" // Icon color
          >
            {/* Path for the arrow shape */}
            <path
              d="M12 4L4 12M12 4L20 12M12 4V20" // SVG path data from the image
              stroke="currentColor" // Use text color for stroke
              strokeWidth="2" // Stroke thickness
              strokeLinecap="round" // Rounded line caps
              strokeLinejoin="round" // Rounded line joins
            />
          </svg>
        </div>
      </div>
    </button>
  );
}; 